import React, { useState } from 'react';
import { useLionAutoFloors, useSaveLionAutoFloors, useFollowLionAutoFloors } from '../../hooks/useLionAuto';
import { MAX_FLOOR_PCT, cleanFloorPct, pctText } from '../../utils/lionAutoLadder';
import { easternText } from '../../utils/lionAutoRunner';
import {
  effectiveFloor, inheritedFloorOf, floorSourceText, floorUseText, parseFloorText, pctInputText, floorStampText,
  floorCountsText, floorChangeConfirm, followConfirm, floorAppliedText, followResultText, floorErrorText, count,
  floorApplyChoices, applyAllBlockedReason, floorAppliedKind,
} from '../../utils/lionAutoFloors';
import LaFloorApplyModal from './LaFloorApplyModal';

// The event's Floors box (% of each listing's cost), above the groups. A
// group with no floor of its own uses this show's floor, else the artist's
// (the Skybox performer: every show of it), else none. Two rows: the artist
// floor (all shows; anyone who can price the artist, so a pricer with access
// to this show by date only sees it read-only) and this show's floor (anyone
// who edits the groups). Hidden artist row: the show's performer is not
// known. The counts line is this show's groups; "make ... follow" clears the
// groups' own floors so they use the inherited one (the artist one covers
// every show of the artist; their show floors stay). Saving a floor above 0
// opens the save dialog (LaFloorApplyModal) when the scope has an exception
// (a group's own floor; for the tour, a show floor too) or the user may
// re-price and an Auto group's floor would change: "Only groups that follow"
// or "Everything" (clears the exceptions), and "Update prices now". Else a
// change that Auto groups follow asks first (they reprice with it on their
// next run), as does a clear; a follow, a bulk edit, always asks. Our DB
// only: nothing is sent to Skybox.
// followBlocked: why no follow (and no "Everything", which clears the same
// own floors) can run now (a saved group is open in the editor: its Save
// would put the cleared floor back), or null. canReprice: the user is
// admin-like ("Update prices now"; the API enforces it).

const groupsText = (n) => `${n} group${n === 1 ? '' : 's'}`;

// One floor: its label, the % box, Save, and who set it. The box keeps the
// pricer's text (text: null = the saved floor shows); a value the server
// would refuse turns it red and keeps Save off, so a half-typed number is
// never saved. A text box (decimal keypad), not type="number": the browser
// would hand a typo such as "1e" over as blank, which clears. Enter saves.
function FloorRow({
  label, labelTitle, saved, text, onText, onSave, locked, lockedTitle, busy, saving, hint, ariaLabel,
}) {
  const shown = text ?? pctInputText(saved?.pct);
  const pct = parseFloorText(shown);
  const bad = pct === null;
  const changed = !bad && pct !== cleanFloorPct(saved?.pct);
  const off = locked || busy;
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!off && changed) onSave(pct);
  };
  let saveTitle = changed ? 'Save this floor (nothing is sent to Skybox)' : 'Type a new floor first';
  if (bad) saveTitle = `Any % from 0.01 to ${MAX_FLOOR_PCT}, cents allowed; blank clears it`;
  if (locked) saveTitle = lockedTitle;
  return (
    <form className="la-floor-row" onSubmit={handleSubmit}>
      <span className="la-floor-label" title={labelTitle}>{label}</span>
      <span className="la-number">
        <input
          type="text"
          inputMode="decimal"
          autoComplete="off"
          className={`form-input form-input-sm${bad ? ' la-input-bad' : ''}`}
          value={shown}
          placeholder="none"
          aria-label={ariaLabel}
          title={locked ? lockedTitle : `Any % up to ${MAX_FLOOR_PCT}, cents allowed; blank clears it`}
          disabled={off}
          onChange={(e) => onText(e.target.value)}
        />
        %
      </span>
      <button type="submit" className="btn btn-primary btn-sm la-floor-save" disabled={off || !changed} title={saveTitle}>
        {saving ? 'Saving…' : 'Save'}
      </button>
      <span className="la-hint" title={easternText(saved?.updatedAt) || undefined}>
        {floorStampText(saved)}{hint ? ` · ${hint}` : ''}
      </span>
    </form>
  );
}

function LaFloorsPanel({ eventId, followBlocked = null, canReprice = false }) {
  const floorsQ = useLionAutoFloors(eventId);
  const saveM = useSaveLionAutoFloors(eventId);
  const followM = useFollowLionAutoFloors(eventId);
  // Each box's text while the pricer edits it; null = the saved floor shows
  const [artistText, setArtistText] = useState(null);
  const [showText, setShowText] = useState(null);
  const [message, setMessage] = useState(null); // { kind: 'ok' | 'warn' | 'error', text } | null
  // The save dialog while open: { scope, pct, opened (its choices on open) } | null
  const [applyAsk, setApplyAsk] = useState(null);

  const f = floorsQ.data || null;
  const httpStatus = floorsQ.error?.response?.status;

  if (!f) {
    return (
      <div className="la-panel la-floors">
        <div className="la-panel-head">
          <h3>Floors</h3>
          <span className="la-muted">% of cost{floorsQ.isLoading ? ' · reading…' : ''}</span>
        </div>
        {floorsQ.error && (
          <div className={`la-status${httpStatus === 404 ? '' : ' error'}`}>
            {floorErrorText(floorsQ.error, { fallback: 'Could not read the floors.', read: true })}
            {httpStatus !== 404 && (
              <button type="button" className="btn btn-secondary btn-sm la-status-action" onClick={() => floorsQ.refetch()}>Retry</button>
            )}
          </div>
        )}
      </div>
    );
  }

  const name = typeof f.performerName === 'string' && f.performerName.trim() ? f.performerName.trim() : null;
  const inherited = inheritedFloorOf(f);
  const inherit = effectiveFloor(0, inherited);
  const here = f.groups?.here || null;
  const artistGroups = f.groups?.artist || null;
  const canEditArtist = !!f.canEditArtist;
  const busy = saveM.isPending || followM.isPending;
  const savingScope = saveM.isPending && saveM.variables ? ('artistPct' in saveM.variables ? 'artist' : 'show') : null;
  const followingScope = followM.isPending ? followM.variables : null;

  const headline = inherit.pct > 0
    ? `% of cost · groups with no floor of their own use ${pctText(inherit.pct)} (${floorSourceText(inherit.source, name)})`
    : '% of cost · no show or artist floor: each group uses its own';

  // One floor write; the flags only from the save dialog. Either way the
  // dialog closes and the result (or the error) shows under the box.
  const runSave = (scope, pct, { applyToAll = false, repriceNow = false } = {}) => {
    const resetText = scope === 'artist' ? setArtistText : setShowText;
    const value = pct > 0 ? pct : null;
    const body = {
      ...(scope === 'artist' ? { artistPct: value } : { showPct: value }),
      ...(applyToAll ? { applyToAll: true } : {}),
      ...(repriceNow ? { repriceNow: true } : {}),
    };
    setMessage(null);
    saveM.mutate(body, {
      onSuccess: (data) => {
        setApplyAsk(null);
        resetText(null);
        setMessage({ kind: floorAppliedKind(data), text: floorAppliedText(data, { scope, pct, performerName: name }) });
      },
      onError: (err) => {
        setApplyAsk(null);
        setMessage({ kind: 'error', text: floorErrorText(err, { eventId }) });
      },
    });
  };

  const handleSave = (scope, pct) => {
    if (busy) return;
    const choices = floorApplyChoices({ scope, pct, floors: f, canReprice });
    if (choices) {
      setMessage(null);
      setApplyAsk({ scope, pct, opened: choices });
      return;
    }
    const ask = floorChangeConfirm({
      scope, pct, performerName: name,
      autoFollowing: scope === 'artist' ? artistGroups?.autoFollowing : here?.autoFollowing,
    });
    if (ask && !window.confirm(ask)) return;
    runSave(scope, pct);
  };

  // The dialog's counts follow the floors query while it is open; a re-read
  // that leaves nothing to choose keeps the ones it opened with, so the
  // dialog never vanishes under the pricer
  const applyChoices = applyAsk
    ? floorApplyChoices({ scope: applyAsk.scope, pct: applyAsk.pct, floors: f, canReprice }) || applyAsk.opened
    : null;
  // "Everything" clears the own floors an open group's Save would put back
  const applyAllBlocked = applyAllBlockedReason(applyChoices, followBlocked);

  const handleApplyConfirm = ({ applyToAll, repriceNow }) => {
    if (busy || !applyAsk) return;
    if (applyToAll && applyAllBlocked) return;
    runSave(applyAsk.scope, applyAsk.pct, { applyToAll, repriceNow });
  };
  const handleApplyCancel = () => {
    if (!saveM.isPending) setApplyAsk(null);
  };

  const handleFollow = (scope) => {
    if (busy) return;
    const n = scope === 'artist' ? count(artistGroups?.own) : count(here?.own);
    if (!window.confirm(followConfirm({ scope, count: n, inherited, performerName: name, artistPct: f.artist?.pct }))) return;
    setMessage(null);
    followM.mutate(scope, {
      onSuccess: (data) => setMessage({ kind: 'ok', text: followResultText(data) }),
      onError: (err) => setMessage({ kind: 'error', text: floorErrorText(err, { eventId, fallback: 'Making the groups follow failed.' }) }),
    });
  };

  // "Make ... follow": only while groups have a floor of their own, and only
  // toward a floor that exists (following none would leave them with none)
  const hereOwn = count(here?.own);
  const artistOwn = count(artistGroups?.own);
  const showFollowOff = followBlocked || (inherit.pct > 0
    ? null
    : 'Set a floor for this show or the artist first: following none would leave these groups with no floor');
  const artistFollowOff = followBlocked || (cleanFloorPct(f.artist?.pct) > 0 ? null : `Set the ${name} floor first`);
  const artistLocked = `Only an admin, or a pricer assigned to ${name}, can change the ${name} floor`;
  const showHint = name && f.artist ? `blank = use artist floor (${pctText(f.artist.pct)})` : 'blank = no floor';

  return (
    <div className="la-panel la-floors">
      <div className="la-panel-head">
        <h3>Floors</h3>
        <span className="la-muted">{headline}</span>
      </div>
      {floorsQ.error && (
        <div className="la-hint warn">
          Could not re-read the floors: this is the last copy.{' '}
          <button type="button" className="la-link" onClick={() => floorsQ.refetch()}>Retry</button>
        </div>
      )}

      <div className="la-floors-rows">
        {name ? (
          <FloorRow
            label={`${name} (all shows)`}
            labelTitle={`Every ${name} show: the floor of a group with none of its own, unless its show has one`}
            saved={f.artist}
            text={artistText}
            onText={setArtistText}
            onSave={(pct) => handleSave('artist', pct)}
            locked={!canEditArtist}
            lockedTitle={artistLocked}
            busy={busy}
            saving={savingScope === 'artist'}
            ariaLabel={`${name} floor, % of cost`}
          />
        ) : (
          <div className="la-hint">This show&apos;s performer is not known, so it has no artist floor: set this show&apos;s floor.</div>
        )}
        <FloorRow
          label="This show only"
          labelTitle="This show's groups with no floor of their own; it wins over the artist floor"
          saved={f.show}
          text={showText}
          onText={setShowText}
          onSave={(pct) => handleSave('show', pct)}
          locked={false}
          busy={busy}
          saving={savingScope === 'show'}
          hint={showHint}
          ariaLabel="This show's floor, % of cost"
        />
      </div>

      <div className="la-floors-foot">
        <span className="la-hint">{floorCountsText(here)}</span>
        {hereOwn > 0 && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={busy || !!showFollowOff}
            title={showFollowOff || `Clear their own floors: they use ${floorUseText(inherit.source, name)} (${pctText(inherit.pct)})`}
            onClick={() => handleFollow('show')}
          >
            {followingScope === 'show' ? 'Working…' : `Make this show's ${groupsText(hereOwn)} follow`}
          </button>
        )}
        {name && canEditArtist && artistOwn > 0 && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={busy || !!artistFollowOff}
            title={artistFollowOff || `Clear the own floors of ${groupsText(artistOwn)} on every ${name} show (show floors stay)`}
            onClick={() => handleFollow('artist')}
          >
            {followingScope === 'artist' ? 'Working…' : `Make all ${artistOwn} ${name} group${artistOwn === 1 ? '' : 's'} follow`}
          </button>
        )}
      </div>
      {message && <div className={`la-status${message.kind === 'ok' ? '' : ` ${message.kind}`}`}>{message.text}</div>}
      {applyChoices && (
        <LaFloorApplyModal
          choices={applyChoices}
          performerName={name}
          allBlocked={applyAllBlocked}
          saving={saveM.isPending}
          onConfirm={handleApplyConfirm}
          onCancel={handleApplyCancel}
        />
      )}
    </div>
  );
}

export default LaFloorsPanel;
